import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import ContratoAnexo from '../components/ContratoAnexo';
import '../styles/bporh_admissoes.css';

// Controle de Estágio (BPO RH / DP) — Lei 11.788/2008.
// Dois relógios por estagiário: o FIM DO TERMO de compromisso vigente (cabe
// renovar por aditivo) e o TETO LEGAL de 2 anos na mesma empresa (art. 11),
// que NÃO admite renovação. Os dois acendem 15 dias antes (ESTAGIO_ALERTA_DIAS
// no backend) — o valor chega em `alerta_dias`, nunca é fixo aqui.
// Mesmo esqueleto da Prorrogação: cabeçalho padrão, empresas em cards, tabela,
// modais de header branco. O termo assinado (PDF) é POR PERÍODO (estagio_id).

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

const ESTOURADO = ['atrasado', 'hoje', 'critico'];
const estourado = (s) => ESTOURADO.includes(s);

// cor = ESTADO, como no resto do app (vermelho: precisa de ação; verde: ok;
// cinza: sem data / histórico)
const SELO = {
  atrasado: { bg: '#fdeaec', cor: '#a11d2b', borda: '#dc3545', icone: 'fa-triangle-exclamation' },
  hoje: { bg: '#fdeaec', cor: '#a11d2b', borda: '#dc3545', icone: 'fa-bell' },
  critico: { bg: '#fdeaec', cor: '#a11d2b', borda: '#dc3545', icone: 'fa-hourglass-half' },
  ok: { bg: '#e6f7ef', cor: '#116b47', borda: '#1fa971', icone: 'fa-circle-check' },
  sem_data: { bg: '#f8f9fa', cor: '#6c757d', borda: '#dee2e6', icone: 'fa-minus' },
};

const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hojeIso = () => isoLocal(new Date());
const diaSeguinte = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + 1);
  return isoLocal(d);
};
// dd/mm/aaaa (como o backend formata) -> aaaa-mm-dd, para o <input type="date">
const brParaIso = (br) => (br && /^\d{2}\/\d{2}\/\d{4}$/.test(br)
  ? `${br.slice(6, 10)}-${br.slice(3, 5)}-${br.slice(0, 2)}` : '');
const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Selo de prazo. `teto` muda o vocabulário: o limite legal não "vence", é
// "atingido"; `estimado` ganha o asterisco (sem admissão na Convenia, a data
// sai do primeiro termo).
const Selo = ({ situacao, dias, estimado, teto, tetoAnos }) => {
  if (!situacao || situacao === 'sem_data' || dias === null || dias === undefined) {
    return <span className="badge bg-light text-muted border">—</span>;
  }
  const s = SELO[situacao] || SELO.sem_data;
  const texto = dias < 0
    ? `${teto ? 'atingido' : 'vencido'} há ${Math.abs(dias)} d`
    : dias === 0 ? (teto ? 'limite hoje' : 'vence hoje') : `em ${dias} d`;
  const title = estimado
    ? 'Sem admissão na Convenia — calculado pelo primeiro termo'
    : teto
      ? `Limite legal de ${tetoAnos} anos na mesma empresa (Lei 11.788, art. 11) — não cabe renovação`
      : 'Fim do termo de compromisso vigente';
  return (
    <span className="badge" title={title}
      style={{ backgroundColor: s.bg, color: s.cor, border: `1.5px solid ${s.borda}` }}>
      <i className={`fas ${s.icone} me-1`}></i>{texto}{estimado ? ' *' : ''}
    </span>
  );
};

// classificação de cada pessoa (a mesma régua do Painel do Operador)
const motivoDe = (it) => {
  if (it.saiu_do_vinculo || it.encerrado) return 'historico';
  // teto estourado vem antes: ele sai da admissão e existe mesmo sem termo cadastrado
  if (estourado(it.teto_situacao)) return 'teto';
  if (it.sem_contrato) return 'sem_contrato';
  if (estourado(it.situacao)) return 'termo';
  return 'ok';
};

const FORM_VAZIO = {
  id: null, modo: 'novo', manual: false, convenia_id: null, chave: null, nome: '',
  colaborador: '', cpf: '', inicio: '', fim: '', instituicao: '', curso: '', supervisor: '', obs: '',
};

const BpoRhEstagio = () => {
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [clientes, setClientes] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteId = searchParams.get('cliente') || '';
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [buscaEmpresa, setBuscaEmpresa] = useState('');
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('');        // '', termo, teto, sem_contrato, historico
  const [aberto, setAberto] = useState({});        // chave -> histórico de períodos aberto
  const [avisoTeto, setAvisoTeto] = useState('');  // aviso do backend ao gravar: fica na tela

  const [form, setForm] = useState(null);          // cadastro / renovação / edição
  const [salvando, setSalvando] = useState(false);
  const [encerrando, setEncerrando] = useState(null);   // {item, motivo, quando}
  const [excluindo, setExcluindo] = useState(null);     // {item, periodo}
  const [previa, setPrevia] = useState(null);           // prévia do e-mail ao gestor
  const [enviando, setEnviando] = useState(false);
  const [editandoCorpo, setEditandoCorpo] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/bporh-clientes/`, { headers: headers() })
      .then((r) => setClientes(r.data.clientes || []))
      .catch(() => setMensagem('❌ Não foi possível carregar as empresas'));
    // quem assina o envio do aviso — padrão da casa (mesmo seletor da prorrogação)
    axios.get(`${API}/api/bporh/operadores/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => setOperadores((r.data.operadores || []).filter((o) => o.ativo !== false)))
      .catch(() => setOperadores([]));
  }, []);

  // ─── relatório geral ao cliente (21/10/2026): planilha + e-mail com mensagem editável ───
  const URL_REL = `${API}/api/bporh/estagios/relatorio/`;
  const [rel, setRel] = useState(null);        // {para, copia, operador_id, assunto, mensagem, operadores, historico}
  const [relPrevia, setRelPrevia] = useState(null);
  const [relOcupado, setRelOcupado] = useState('');
  const erroRel = (e, padrao) => e.response?.data?.mensagem || e.response?.data?.erro || padrao;
  const abrirRelatorio = () => {
    setRelOcupado('abrir');
    axios.get(URL_REL, { params: { cliente_id: clienteId }, headers: headers() })
      .then((r) => {
        const e = r.data.envio || {};
        setRel({ para: e.para || '', copia: e.copia || '', operador_id: String(e.operador_sugerido_id || ''),
                 assunto: e.assunto || '', mensagem: e.mensagem || '', operadores: e.operadores || [],
                 historico: e.historico || [] });
        setRelPrevia(null);
      })
      .catch((e) => notificar(`❌ ${erroRel(e, 'Não foi possível abrir o relatório')}`))
      .finally(() => setRelOcupado(''));
  };
  const mudarRel = (campo, valor) => { setRel((x) => ({ ...x, [campo]: valor })); setRelPrevia(null); };
  const baixarRelatorio = () => {
    setRelOcupado('baixar');
    axios.get(URL_REL, { params: { cliente_id: clienteId, exportar: 1, _: Date.now() }, headers: headers(), responseType: 'blob' })
      .then((r) => {
        const m = (r.headers['content-disposition'] || '').match(/filename="?([^";]+)"?/i);
        const url = window.URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = m ? m[1] : 'Relatorio_Estagiarios.xlsx';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
      })
      .catch(() => notificar('❌ Não foi possível gerar a planilha'))
      .finally(() => setRelOcupado(''));
  };
  const corpoRel = (extra) => ({ cliente_id: Number(clienteId), destinatarios: rel.para, copia: rel.copia,
    operador_id: rel.operador_id || undefined, assunto: rel.assunto, mensagem: rel.mensagem, ...extra });
  const verPreviaRel = () => {
    setRelOcupado('previa');
    axios.post(URL_REL, corpoRel({ acao: 'email', enviar: false }), { headers: headers(), timeout: 120000 })
      .then((r) => setRelPrevia(r.data.previa))
      .catch((e) => notificar(`❌ ${erroRel(e, 'Não foi possível montar a prévia')}`))
      .finally(() => setRelOcupado(''));
  };
  const salvarEmailsRel = () => {
    setRelOcupado('salvar');
    axios.post(URL_REL, corpoRel({ acao: 'salvar_emails' }), { headers: headers() })
      .then((r) => notificar(`✅ ${r.data.mensagem}`))
      .catch((e) => notificar(`❌ ${erroRel(e, 'Não foi possível salvar os e-mails')}`))
      .finally(() => setRelOcupado(''));
  };
  const enviarRel = () => {
    setRelOcupado('enviar');
    axios.post(URL_REL, corpoRel({ acao: 'email', enviar: true }), { headers: headers(), timeout: 180000 })
      .then((r) => { notificar(`✅ ${r.data.mensagem}`); setRel(null); setRelPrevia(null); })
      .catch((e) => notificar(`❌ ${erroRel(e, 'Não foi possível enviar')}`))
      .finally(() => setRelOcupado(''));
  };

  const carregar = useCallback(() => {
    if (!clienteId) { setDados(null); return; }
    // outra empresa: some com a anterior já — senão a tabela e o título
    // mostram o cliente errado até a resposta chegar (e ficam, se falhar)
    setDados((d) => (d && d.cliente_id === Number(clienteId) ? d : null));
    setCarregando(true);
    axios.get(`${API}/api/bporh/estagios/`, { params: { cliente_id: clienteId, _: Date.now() }, headers: headers() })
      .then((r) => setDados(r.data))
      .catch((e) => setMensagem(`❌ ${e.response?.data?.erro || 'Não foi possível carregar os estágios'}`))
      .finally(() => setCarregando(false));
  }, [clienteId]);
  useEffect(() => { carregar(); }, [carregar]);

  const clienteAtual = useMemo(() => clientes.find((c) => String(c.id) === String(clienteId)) || null, [clientes, clienteId]);
  const alertaDias = dados?.alerta_dias ?? 15;
  const tetoDias = dados?.teto_alerta_dias ?? 30;
  const tetoAnos = dados?.teto_anos ?? 2;

  const abrirEmpresa = (c) => { setBusca(''); setFiltro(''); setAberto({}); setAvisoTeto(''); setSearchParams({ cliente: String(c.id) }); };
  const voltarEmpresas = () => { setDados(null); setSearchParams({}); };

  const itens = useMemo(() => {
    const lista = (dados?.estagios || []).map((it) => ({ ...it, _motivo: motivoDe(it) }));
    const peso = { teto: 0, termo: 1, sem_contrato: 2, ok: 3, historico: 4 };
    const b = norm(busca.trim());
    return lista
      .filter((it) => {
        if (filtro && it._motivo !== filtro) return false;
        if (!b) return true;
        const p = it.periodo_atual || it.periodos?.[0] || {};
        return norm([it.nome, it.cargo, it.departamento, p.instituicao, p.curso, p.supervisor]
          .filter(Boolean).join(' ')).includes(b);
      })
      .sort((a, z) => (peso[a._motivo] - peso[z._motivo])
        || ((a._motivo === 'teto' ? a.teto_dias : a.dias) ?? 9999) - ((z._motivo === 'teto' ? z.teto_dias : z.dias) ?? 9999)
        || norm(a.nome).localeCompare(norm(z.nome)));
  }, [dados, busca, filtro]);

  const painel = useMemo(() => {
    const lista = (dados?.estagios || []).map(motivoDe);
    const n = (m) => lista.filter((x) => x === m).length;
    return {
      ativos: lista.filter((x) => x !== 'historico').length,
      termo: n('termo'), teto: n('teto'), sem_contrato: n('sem_contrato'), historico: n('historico'),
    };
  }, [dados]);

  // ── cadastro / renovação / edição de período ──
  const abrirNovo = (item, modo) => {
    const atual = item?.periodo_atual || item?.periodos?.[0] || null;
    setForm({
      ...FORM_VAZIO,
      modo, manual: !item,
      convenia_id: item?.convenia_id || null, chave: item?.chave || null,
      // o CPF é a chave do estagiário sem Convenia: sem ele o aditivo viraria
      // outra pessoa na lista e o teto contaria do aditivo (só vem no manual)
      nome: item?.nome || '', colaborador: item?.nome || '', cpf: item?.cpf || '',
      // renovação: o aditivo começa no dia seguinte ao fim do termo vigente
      inicio: modo === 'renovar' ? diaSeguinte(atual?.fim_iso) : (modo === 'novo' ? brParaIso(item?.admissao) : ''),
      fim: '',
      instituicao: atual?.instituicao || '', curso: atual?.curso || '', supervisor: atual?.supervisor || '',
      obs: '',
    });
  };
  const abrirEdicao = (item, p) => setForm({
    ...FORM_VAZIO, id: p.id, modo: 'editar', nome: item.nome, chave: item.chave,
    // sem Convenia, nome e CPF são editáveis aqui (o CPF é a chave da pessoa)
    manual: item.origem === 'manual', colaborador: item.nome || '', cpf: item.cpf || '',
    inicio: p.inicio_iso || '', fim: p.fim_iso || '',
    instituicao: p.instituicao || '', curso: p.curso || '', supervisor: p.supervisor || '', obs: p.obs || '',
  });

  // "Ler termo com a IA": o Gemini preenche o formulário; o PDF fica guardado no
  // form e é anexado ao período assim que ele for gravado (01/10/2026)
  const lerTermo = (arq) => {
    if (!arq) return;
    const fd = new FormData();
    fd.append('cliente_id', clienteId); fd.append('arquivo', arq);
    if (form.convenia_id) fd.append('convenia_id', form.convenia_id);
    if (form.cpf) fd.append('cpf', form.cpf);
    fd.append('nome', form.colaborador || form.nome || '');
    setForm((f) => ({ ...f, termoLendo: true, termoAlertas: [], termoObs: [] }));
    axios.post(`${API}/api/bporh/estagios/ler-termo/`, fd, { headers: headers() })
      .then((r) => {
        const c = r.data.campos || {};
        setForm((f) => ({
          ...f, termoLendo: false, termoArquivo: arq,
          termoAlertas: r.data.alertas || [], termoObs: r.data.observacoes || [], termoAditivo: !!c.aditivo,
          inicio: c.inicio || f.inicio, fim: c.fim || f.fim,
          instituicao: c.instituicao || f.instituicao, curso: c.curso || f.curso,
          supervisor: c.supervisor || f.supervisor, obs: c.obs || f.obs,
          colaborador: f.manual && !f.colaborador ? (c.estagiario || '') : f.colaborador,
          cpf: f.manual && !f.cpf ? (c.cpf || '') : f.cpf,
        }));
        setMensagem(`✅ ${r.data.mensagem}`);
      })
      .catch((e) => {
        setForm((f) => ({ ...f, termoLendo: false }));
        setMensagem(`❌ ${e.response?.data?.mensagem || 'Não foi possível ler o termo'}`);
      });
  };
  const anexarTermo = (arq, estagioId, chavePessoa, nome) => {
    const fd = new FormData();
    fd.append('tipo', 'estagio'); fd.append('cliente_id', clienteId);
    fd.append('convenia_id', chavePessoa); fd.append('estagio_id', estagioId);
    fd.append('colaborador', nome || ''); fd.append('tipo_contrato', 'Estágio');
    fd.append('arquivo', arq);
    return axios.post(`${API}/api/bporh/contrato/`, fd, { headers: headers() });
  };

  const salvar = () => {
    if (!form.inicio || !form.fim) { setMensagem('⚠️ Informe o início e o fim do período'); return; }
    if (form.fim < form.inicio) { setMensagem('⚠️ O fim do período é anterior ao início'); return; }
    if (form.manual && !form.colaborador.trim()) { setMensagem('⚠️ Informe o nome do estagiário'); return; }
    // início repetido: quase sempre é aditivo lançado duas vezes ou data errada —
    // avisa no modal e pede um segundo clique, sem bloquear (é decisão do operador)
    const pessoa = (dados?.estagios || []).find((x) => x.chave === form.chave);
    const repetido = (pessoa?.periodos || []).find((p) => p.id !== form.id && p.inicio_iso === form.inicio);
    if (repetido && form.confirmarInicio !== form.inicio) {
      setForm((f) => ({ ...f, avisoInicio: repetido.inicio, confirmarInicio: form.inicio }));
      return;
    }
    const campos = {
      inicio: form.inicio, fim: form.fim, instituicao: form.instituicao, curso: form.curso,
      supervisor: form.supervisor, obs: form.obs,
    };
    setSalvando(true);
    const req = form.id
      ? axios.put(`${API}/api/bporh/estagios/${form.id}/`,
        form.manual ? { ...campos, colaborador: form.colaborador, cpf: form.cpf } : campos,
        { headers: headers() })
      : axios.post(`${API}/api/bporh/estagios/`, {
        ...campos, cliente_id: Number(clienteId),
        convenia_id: form.convenia_id || undefined,
        colaborador: form.colaborador || form.nome || undefined,
        cpf: !form.convenia_id ? (form.cpf || undefined) : undefined,
      }, { headers: headers() });
    req
      .then((r) => {
        // o aviso de teto NÃO impede o salvamento (há o caso de PcD, art. 11):
        // o operador decide — por isso fica em destaque na tela, não só no toast
        if (r.data.aviso_teto) { setAvisoTeto(r.data.aviso_teto); setMensagem(`⚠️ ${r.data.aviso_teto}`); }
        else { setAvisoTeto(''); setMensagem(`✅ ${r.data.mensagem || 'Período gravado'}`); }
        const arq = form.termoArquivo;
        const periodoId = form.id || r.data.id;
        const chavePessoa = form.convenia_id || form.chave || (form.cpf ? `manual:${String(form.cpf).replace(/\D/g, '')}` : '');
        setForm(null);
        if (arq && periodoId && chavePessoa) {
          anexarTermo(arq, periodoId, chavePessoa, form.colaborador || form.nome)
            .then(() => setMensagem('✅ Período gravado e termo anexado'))
            .catch((e) => setMensagem(`⚠️ Período gravado, mas o PDF não foi anexado: ${e.response?.data?.mensagem || 'erro'} — anexe pela coluna "Termo assinado"`))
            .finally(() => carregar());
        } else {
          carregar();
        }
      })
      .catch((e) => setMensagem(`❌ ${e.response?.data?.mensagem || e.response?.data?.erro || 'Não foi possível gravar'}`))
      .finally(() => setSalvando(false));
  };

  const encerrar = () => {
    const { item, motivo, quando } = encerrando;
    if (!motivo.trim()) { setMensagem('⚠️ Informe o motivo do encerramento'); return; }
    setSalvando(true);
    axios.post(`${API}/api/bporh/estagios/${item.periodo_atual.id}/encerrar/`,
      { motivo: motivo.trim(), encerrado_em: quando || undefined }, { headers: headers() })
      .then((r) => { setMensagem(`✅ ${r.data.mensagem}`); setEncerrando(null); carregar(); })
      .catch((e) => setMensagem(`❌ ${e.response?.data?.mensagem || 'Não foi possível encerrar'}`))
      .finally(() => setSalvando(false));
  };

  const excluir = () => {
    setSalvando(true);
    axios.delete(`${API}/api/bporh/estagios/${excluindo.periodo.id}/`, { headers: headers() })
      .then((r) => { setMensagem(`✅ ${r.data.mensagem}`); setExcluindo(null); carregar(); })
      .catch((e) => setMensagem(`❌ ${e.response?.data?.mensagem || 'Não foi possível excluir'}`))
      .finally(() => setSalvando(false));
  };

  // ── aviso ao gestor: prévia + envio (o Enviar mora DENTRO da prévia) ──
  const operadorSugerido = () => {
    // a operadora da empresa (campo "operador" do cadastro) é quem normalmente assina
    const alvo = norm(clienteAtual?.operador);
    if (!alvo) return '';
    const op = operadores.find((o) => o.tem_senha && (norm(o.nome) === alvo
      || (o.apelidos || '').split(/[,/;|]+/).some((a) => a.trim() && norm(a.trim()) === alvo)));
    return op ? String(op.id) : '';
  };
  const abrirPrevia = (item) => {
    const estagioId = item.periodo_atual?.id;
    if (!estagioId) return;
    setEditandoCorpo(false);
    setPrevia({ carregando: true, item });
    axios.get(`${API}/api/bporh/estagios/aviso/`,
      { params: { cliente_id: clienteId, estagio_id: estagioId }, headers: headers() })
      // destinatários vêm do e-mail salvo (do estagiário ou padrão da empresa);
      // o grupo de e-mail do cliente não entra aqui — o backend o põe em cc/bcc no envio.
      .then((r) => setPrevia({
        ...r.data, item, estagio_id: estagioId, carregando: false,
        destinatarios: (r.data.destinatarios_sugeridos || []).join('; '),
        operador_id: operadorSugerido(),
      }))
      .catch((e) => { setPrevia(null); setMensagem(`❌ ${e.response?.data?.erro || 'Não foi possível montar a prévia'}`); });
  };
  const salvarEmail = () => {
    setPrevia((p) => ({ ...p, salvandoEmail: true }));
    axios.post(`${API}/api/bporh/estagios/aviso/`, {
      cliente_id: Number(clienteId), estagio_id: previa.estagio_id,
      destinatarios: previa.destinatarios, salvar_email: true,
    }, { headers: headers() })
      .then((r) => { setMensagem(`✅ ${r.data.mensagem}`); setPrevia((p) => ({ ...p, salvandoEmail: false, destinatarios_origem: 'pessoa' })); })
      .catch((e) => { setMensagem(`❌ ${e.response?.data?.erro || 'Não foi possível salvar'}`); setPrevia((p) => ({ ...p, salvandoEmail: false })); });
  };
  const enviar = () => {
    if (!previa.operador_id) { setMensagem('⚠️ Escolha a operadora que assina o envio'); return; }
    if (!previa.destinatarios.trim()) { setMensagem('⚠️ Informe ao menos um destinatário'); return; }
    setEnviando(true);
    axios.post(`${API}/api/bporh/estagios/aviso/`, {
      cliente_id: Number(clienteId), estagio_id: previa.estagio_id,
      destinatarios: previa.destinatarios, assunto: previa.assunto,
      corpo_html: previa.corpo_html, operador_id: previa.operador_id,
    }, { headers: headers() })
      .then((r) => { setMensagem(`✅ ${r.data.mensagem}`); setPrevia(null); carregar(); })
      .catch((e) => setMensagem(`❌ ${e.response?.data?.erro || e.response?.data?.mensagem || 'Não foi possível enviar'}`))
      .finally(() => setEnviando(false));
  };

  const clientesFiltrados = useMemo(() => {
    const b = norm(buscaEmpresa.trim());
    return clientes.filter((c) => !b || norm(`${c.cliente} ${c.operador || ''}`).includes(b));
  }, [clientes, buscaEmpresa]);

  // ── uma linha por pessoa ──
  const linha = (it) => {
    const historico = it._motivo === 'historico';
    const noTeto = it._motivo === 'teto';
    const atual = it.periodo_atual;
    const ultimo = atual || it.periodos?.[0] || null;
    const chaveAnexo = it.convenia_id || it.chave;   // manual: 'manual:<cpf>', a mesma chave da listagem
    // manual sem CPF: a chave é o id do período, então um aditivo viraria OUTRA
    // pessoa na lista (e o limite de 2 anos recomeçaria) — renovação travada
    const semCpf = it.origem === 'manual' && !it.cpf;
    const TIP_CPF = 'cadastre o CPF em Editar para renovar — sem ele o aditivo vira outra pessoa';
    const historicoAberto = !!aberto[it.chave];
    const nPeriodos = (it.periodos || []).length;
    return (
      <React.Fragment key={it.chave}>
        <tr style={historico ? { color: '#6c757d', background: '#fafbfc' } : undefined}>
          <td className="fw-bold">
            {it.nome}
            <div className="small text-muted fw-normal">
              {[it.cargo, it.departamento].filter(Boolean).join(' · ') || (it.origem === 'manual' ? 'cadastro manual' : '—')}
            </div>
            {ultimo && (ultimo.curso || ultimo.instituicao) && (
              <div className="small text-muted fw-normal">
                <i className="fas fa-graduation-cap me-1"></i>
                {[ultimo.curso, ultimo.instituicao].filter(Boolean).join(' · ')}
              </div>
            )}
            {it.origem === 'manual' && (
              <span className="badge bg-light text-muted border mt-1" title="Estagiário cadastrado à mão, sem espelho na Convenia">
                sem Convenia
              </span>
            )}
            {it.saiu_do_vinculo && (
              <span className="badge bg-light text-muted border mt-1"
                title="Deixou de constar como estagiário na Convenia (efetivado ou desligado) — fica como histórico, sem prazo">
                <i className="fas fa-box-archive me-1"></i>efetivado ou desligado
              </span>
            )}
          </td>
          <td style={{ whiteSpace: 'nowrap' }}>{it.admissao || '—'}</td>
          <td style={{ whiteSpace: 'nowrap' }}>
            {atual ? (
              <>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{atual.inicio} → {atual.fim}</span>
                {atual.supervisor && <div className="small text-muted">supervisor: {atual.supervisor}</div>}
              </>
            ) : it.sem_contrato ? (
              <span className="badge" title="Nenhum termo de compromisso cadastrado — o limite de 2 anos conta da admissão da Convenia"
                style={{ backgroundColor: historico ? '#f8f9fa' : '#fdeaec', color: historico ? '#6c757d' : '#a11d2b',
                  border: `1.5px solid ${historico ? '#dee2e6' : '#dc3545'}` }}>
                <i className="fas fa-file-circle-question me-1"></i>sem contrato cadastrado
              </span>
            ) : ultimo ? (
              <>
                <span className="badge bg-light text-muted border" title={ultimo.encerrado_motivo || ''}>
                  <i className="fas fa-box-archive me-1"></i>encerrado {ultimo.encerrado_em || ''}
                </span>
                <div className="small text-muted">{ultimo.inicio} → {ultimo.fim}{ultimo.encerrado_motivo ? ` · ${ultimo.encerrado_motivo}` : ''}</div>
              </>
            ) : '—'}
            {nPeriodos > 0 && (
              <div>
                <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: '.76rem', textDecoration: 'none' }}
                  aria-expanded={historicoAberto}
                  onClick={() => setAberto((a) => ({ ...a, [it.chave]: !historicoAberto }))}>
                  <i className={`fas fa-chevron-${historicoAberto ? 'up' : 'down'} me-1`}></i>
                  {nPeriodos} período{nPeriodos > 1 ? 's' : ''}
                </button>
              </div>
            )}
          </td>
          <td style={{ whiteSpace: 'nowrap' }}>
            {historico || !atual
              ? <span className="badge bg-light text-muted border">—</span>
              : <Selo situacao={it.situacao} dias={it.dias} />}
          </td>
          <td style={{ whiteSpace: 'nowrap' }}>
            {it.teto && (
              <div style={{ fontVariantNumeric: 'tabular-nums' }}
                title={it.teto_estimado ? 'Sem admissão na Convenia — calculado pelo primeiro termo' : `Último dia legal: ${tetoAnos} anos após a admissão`}>
                {it.teto}{it.teto_estimado ? ' *' : ''}
              </div>
            )}
            {historico
              ? <span className="badge bg-light text-muted border">—</span>
              : <Selo situacao={it.teto_situacao} dias={it.teto_dias} estimado={it.teto_estimado} teto tetoAnos={tetoAnos} />}
            {noTeto && (
              <div className="small fw-semibold" style={{ color: '#a11d2b' }}>não cabe renovação</div>
            )}
          </td>
          <td>
            {atual && !historico ? (
              <ContratoAnexo
                tipo="estagio"
                clienteId={Number(clienteId)}
                conveniaId={chaveAnexo}
                estagioId={atual.id}
                colaborador={it.nome}
                vinculo="Estágio"
                aviso={false}
                onMudou={carregar}
              />
            ) : <span className="small text-muted">{atual ? '—' : 'cadastre o termo'}</span>}
          </td>
          <td style={{ whiteSpace: 'nowrap' }}>
            {it.sem_contrato && !it.saiu_do_vinculo && (
              <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2 me-1"
                title="Cadastrar o termo de compromisso inicial" onClick={() => abrirNovo(it, 'novo')}>
                <i className="fas fa-file-circle-plus me-1"></i>Cadastrar termo
              </button>
            )}
            {it.encerrado && !it.saiu_do_vinculo && (
              <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2 me-1" disabled={semCpf}
                title={semCpf ? TIP_CPF : 'A pessoa segue como estagiária: cadastre o novo termo'} onClick={() => abrirNovo(it, 'novo')}>
                <i className="fas fa-file-circle-plus me-1"></i>Novo termo
              </button>
            )}
            {atual && !historico && (
              <>
                <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2 me-1"
                  title={noTeto
                    ? `Avisar o gestor: limite legal de ${tetoAnos} anos — encerrar ou efetivar`
                    : 'Avisar o gestor: o termo vence — renovar ou encerrar?'}
                  onClick={() => abrirPrevia(it)}>
                  <i className="fas fa-envelope me-1"></i>Avisar gestor
                </button>
                {/* no teto não há renovação a oferecer: o caminho é encerrar ou efetivar */}
                {!noTeto && (
                  <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2 me-1" disabled={semCpf}
                    title={semCpf ? TIP_CPF : 'Cadastrar o aditivo de renovação (começa no dia seguinte ao fim do termo vigente)'}
                    onClick={() => abrirNovo(it, 'renovar')}>
                    <i className="fas fa-rotate me-1"></i>Renovar
                  </button>
                )}
                <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2"
                  title="Encerrar o estágio antes do fim (efetivação, desistência, desligamento)"
                  onClick={() => setEncerrando({ item: it, motivo: '', quando: hojeIso() })}>
                  <i className="fas fa-flag-checkered me-1"></i>Encerrar
                </button>
              </>
            )}
          </td>
        </tr>
        {historicoAberto && nPeriodos > 0 && (
          <tr style={{ background: '#f8f9fb' }}>
            <td colSpan={7} className="py-2">
              <div className="small fw-semibold mb-2" style={{ color: '#00205b' }}>
                <i className="fas fa-clock-rotate-left me-1"></i>Períodos de estágio — termo inicial e aditivos (o mais novo primeiro)
              </div>
              <div className="d-flex flex-column gap-2">
                {it.periodos.map((p) => (
                  <div key={p.id} className="border rounded bg-white p-2"
                    style={{ opacity: p.encerrado ? 0.75 : 1 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{p.inicio} → {p.fim}</span>
                      {atual && p.id === atual.id && (
                        <span className="badge" style={{ backgroundColor: '#e6f7ef', color: '#116b47', border: '1.5px solid #1fa971' }}>vigente</span>
                      )}
                      {p.encerrado && (
                        <span className="badge bg-light text-muted border" title={p.encerrado_motivo || ''}>
                          encerrado {p.encerrado_em || ''}{p.encerrado_motivo ? ` · ${p.encerrado_motivo}` : ''}
                        </span>
                      )}
                      <span className="small text-muted">
                        {[p.instituicao, p.curso, p.supervisor && `supervisor: ${p.supervisor}`].filter(Boolean).join(' · ')}
                      </span>
                      <span className="ms-auto d-flex gap-1">
                        <button type="button" className="btn btn-sm btn-light border py-0 px-2" style={{ fontSize: '.74rem' }}
                          title="Editar datas, instituição, curso, supervisor ou observação"
                          onClick={() => abrirEdicao(it, p)}>
                          <i className="fas fa-pen me-1"></i>Editar
                        </button>
                        <button type="button" className="btn btn-sm btn-light border py-0 px-2" style={{ fontSize: '.74rem' }}
                          title="Excluir este período (o anexo dele sai junto)"
                          onClick={() => setExcluindo({ item: it, periodo: p })}>
                          <i className="fas fa-trash text-danger me-1"></i>Excluir
                        </button>
                      </span>
                    </div>
                    {p.obs && <div className="small text-muted mt-1"><i className="fas fa-note-sticky me-1"></i>{p.obs}</div>}
                    {/* o termo assinado é deste período — cada aditivo tem o seu */}
                    <ContratoAnexo
                      tipo="estagio"
                      clienteId={Number(clienteId)}
                      conveniaId={chaveAnexo}
                      estagioId={p.id}
                      colaborador={it.nome}
                      vinculo="Estágio"
                      aviso={false}
                      onMudou={carregar}
                    />
                  </div>
                ))}
                {noTeto && (
                  <div className="small text-muted">
                    <i className="fas fa-circle-info me-1"></i>
                    O limite legal de {tetoAnos} anos foi alcançado: não cabe aditivo. Só a exceção da lei
                    (estagiário com deficiência, art. 11) admite seguir —{' '}
                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline" style={{ fontSize: 'inherit' }}
                      disabled={semCpf} title={semCpf ? TIP_CPF : undefined}
                      onClick={() => abrirNovo(it, 'renovar')}>
                      cadastrar período excepcional
                    </button>.
                  </div>
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="bporh-admissoes-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho padrão: card branco, ícone dourado, título preto, traço ciano */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-graduation-cap header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Controle de Estágio
              {clienteId && <span className="text-muted"> · {dados?.cliente || clienteAtual?.cliente || ''}</span>}
            </h2>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {clienteId && (
              <>
                <button className="btn btn-outline-secondary btn-sm" onClick={voltarEmpresas}>
                  <i className="fas fa-arrow-left me-2"></i>Empresas
                </button>
                <button className="btn btn-outline-primary btn-sm" onClick={() => abrirNovo(null, 'novo')}
                  title="Estagiário que não está na Convenia: cadastre o termo à mão">
                  <i className="fas fa-user-plus me-2"></i>Estagiário sem Convenia
                </button>
                <button className="btn btn-outline-primary btn-sm" onClick={carregar} disabled={carregando}>
                  <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>Atualizar
                </button>
                <button className="btn btn-primary btn-sm" onClick={abrirRelatorio} disabled={!!relOcupado}
                  title="Compila todos os estagiários num relatório só (planilha) e manda ao cliente por e-mail">
                  <i className={`fas ${relOcupado === 'abrir' ? 'fa-spinner fa-spin' : 'fa-file-lines'} me-2`}></i>Relatório geral
                </button>
              </>
            )}
          </div>
        </div>

        <div className="alert alert-light border small mb-3">
          <i className="fas fa-info-circle me-2 text-primary"></i>
          Termos de compromisso de estágio (Lei 11.788/2008). Dois prazos por estagiário: o <b>fim do termo</b>{' '}
          vigente, que cabe renovar por aditivo, e o <b>limite legal de {tetoAnos} anos</b> na mesma empresa,
          que <b>não admite renovação</b> — ali o caminho é encerrar ou efetivar. O termo acende{' '}
          <b>{alertaDias} dias</b> antes e o limite legal, <b>{tetoDias} dias</b> antes. O termo assinado (PDF) é anexado em cada período.
        </div>

        {!clienteId ? (
          <>
            <div className="row g-2 mb-3">
              <div className="col-md-5">
                <input type="text" className="form-control form-control-sm" placeholder="Empresa ou operadora..."
                  value={buscaEmpresa} onChange={(e) => setBuscaEmpresa(e.target.value)} />
              </div>
            </div>
            {clientes.length === 0 ? (
              <div className="alert alert-info"><i className="fas fa-info-circle me-2"></i>Nenhuma empresa ativa no BPO RH.</div>
            ) : (
              <div className="adm-grid">
                {clientesFiltrados.map((c, i) => (
                  <div key={c.id} className="adm-card" style={{ '--i': i }}
                    onClick={() => abrirEmpresa(c)} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') abrirEmpresa(c); }}>
                    <div className="adm-card-top">
                      <h3 className="adm-empresa">{c.cliente}</h3>
                    </div>
                    <div className="adm-operador">
                      <i className="fas fa-user-tie"></i>
                      {c.operador || 'Sem operador'}
                    </div>
                    <div className="adm-card-acao">
                      Ver estagiários <i className="fas fa-arrow-right"></i>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : carregando && !dados ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
            <p className="text-muted mt-2">Carregando estágios...</p>
          </div>
        ) : dados && (
          <>
            {/* painel-resumo */}
            <div className="adm-resumo mb-4">
              <div className="adm-kpi">
                <span className="v">{painel.ativos}</span>
                <span className="k">Estagiários</span>
              </div>
              <div className={`adm-kpi ${painel.termo ? 'pend' : ''}`}>
                <span className="v">{painel.termo}</span>
                <span className="k">Termo em {alertaDias}d ou vencido</span>
              </div>
              <div className={`adm-kpi ${painel.teto ? 'pend' : ''}`}>
                <span className="v">{painel.teto}</span>
                <span className="k">Limite de {tetoAnos} anos em {tetoDias}d</span>
              </div>
              <div className={`adm-kpi ${painel.sem_contrato ? 'pend' : ''}`}>
                <span className="v">{painel.sem_contrato}</span>
                <span className="k">Sem contrato cadastrado</span>
              </div>
              <div className="adm-kpi">
                <span className="v">{painel.historico}</span>
                <span className="k">Histórico</span>
              </div>
            </div>

            {avisoTeto && (
              <div className="alert d-flex align-items-start gap-2 mb-3"
                style={{ backgroundColor: '#fdeaec', color: '#a11d2b', border: '1px solid #f5c2c7' }}>
                <i className="fas fa-triangle-exclamation mt-1"></i>
                <div className="flex-grow-1">
                  <b>Período gravado, mas passa do limite legal.</b>
                  <div className="small mt-1">{avisoTeto}</div>
                </div>
                <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setAvisoTeto('')}></button>
              </div>
            )}

            <div className="row g-2 mb-3">
              <div className="col-md-5">
                <input type="text" className="form-control form-control-sm"
                  placeholder="Nome, cargo, departamento, curso, instituição, supervisor..."
                  value={busca} onChange={(e) => setBusca(e.target.value)} />
              </div>
              <div className="col-md-4">
                <select className="form-select form-select-sm" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
                  <option value="">Todas as situações</option>
                  <option value="termo">Termo vencendo ou vencido ({alertaDias} dias)</option>
                  <option value="teto">Limite de {tetoAnos} anos ({tetoDias} dias) — não cabe renovar</option>
                  <option value="sem_contrato">Sem contrato cadastrado</option>
                  <option value="ok">Dentro do prazo</option>
                  <option value="historico">Histórico (encerrados, efetivados, desligados)</option>
                </select>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle" style={{ fontSize: '.9rem' }}>
                <thead>
                  <tr>
                    <th><i className="fas fa-user me-2"></i>Estagiário</th>
                    <th><i className="fas fa-calendar-plus me-2"></i>Admissão</th>
                    <th><i className="fas fa-file-signature me-2"></i>Período vigente</th>
                    <th><i className="fas fa-hourglass-half me-2"></i>Fim do termo</th>
                    <th><i className="fas fa-scale-balanced me-2"></i>Limite de {tetoAnos} anos</th>
                    <th><i className="fas fa-file-pdf me-2"></i>Termo assinado</th>
                    <th><i className="fas fa-bolt me-2"></i>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map(linha)}
                  {itens.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        {dados.estagios?.length
                          ? 'Nenhum estagiário com esse filtro.'
                          : 'Nenhum estagiário nesta empresa — a Convenia não marca ninguém como Estágio. Use "Estagiário sem Convenia" se precisar cadastrar à mão.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="small text-muted">
              Limite de 2 anos contado da data de admissão da Convenia. * sem admissão na Convenia: calculado pelo primeiro termo.
            </div>
          </>
        )}

        {/* ─── MODAL: CADASTRAR / RENOVAR / EDITAR PERÍODO ─── */}
        {form && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '640px' }}>
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className={`fas ${form.modo === 'renovar' ? 'fa-rotate' : form.modo === 'editar' ? 'fa-pen' : 'fa-file-circle-plus'} me-2`}></i>
                      {form.modo === 'renovar' ? 'Aditivo de renovação' : form.modo === 'editar' ? 'Editar período' : 'Termo de compromisso'}
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {form.nome || 'Estagiário sem Convenia'}{dados?.cliente ? ` · ${dados.cliente}` : ''}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setForm(null)} disabled={salvando}></button>
                </div>
                <div className="modal-body">
                  {/* ler o termo com a IA: preenche os campos e anexa o PDF ao salvar */}
                  <div className="rounded p-2 mb-3" style={{ background: '#f4f8ff', border: '1px solid #c9d8f5' }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <input type="file" id="termo-ia" hidden accept="application/pdf,.pdf,.png,.jpg,.jpeg,.webp"
                        onChange={(e) => { lerTermo(e.target.files[0]); e.target.value = ''; }} />
                      <label htmlFor="termo-ia" className={`btn btn-sm btn-primary mb-0 ${form.termoLendo ? 'disabled' : ''}`}>
                        <i className={`fas ${form.termoLendo ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'} me-1`}></i>
                        {form.termoLendo ? 'Lendo o termo…' : (form.termoArquivo ? 'Ler outro termo' : 'Ler termo com a IA')}
                      </label>
                      <span className="small text-muted">
                        {form.termoArquivo
                          ? <><i className="fas fa-paperclip me-1"></i><b>{form.termoArquivo.name}</b> — será anexado ao salvar{form.termoAditivo ? ' (termo aditivo)' : ''}</>
                          : 'Suba o PDF do termo: a IA preenche datas, instituição, curso e supervisor, e o PDF é anexado ao salvar.'}
                      </span>
                    </div>
                    {(form.termoAlertas || []).map((a) => (
                      <div key={a} className="small mt-1" style={{ color: '#a11d2b' }}><i className="fas fa-triangle-exclamation me-1"></i>{a}</div>
                    ))}
                    {(form.termoObs || []).map((o) => <div key={o} className="small text-muted mt-1">· {o}</div>)}
                  </div>
                  {form.manual && (
                    <div className="row g-2 mb-2">
                      <div className="col-md-7">
                        <label className="form-label small fw-bold mb-1 text-muted">Nome do estagiário</label>
                        <input type="text" className="form-control form-control-sm" autoFocus
                          value={form.colaborador} onChange={(e) => setForm((f) => ({ ...f, colaborador: e.target.value }))} />
                      </div>
                      <div className="col-md-5">
                        <label className="form-label small fw-bold mb-1 text-muted">CPF (opcional)</label>
                        <input type="text" className="form-control form-control-sm" inputMode="numeric"
                          value={form.cpf} onChange={(e) => setForm((f) => ({ ...f, cpf: e.target.value }))}
                          placeholder="só números" />
                        <small className="text-muted">é a chave da pessoa: sem ele não dá para renovar (o aditivo viraria outra pessoa)</small>
                      </div>
                    </div>
                  )}
                  <div className="row g-2 mb-2">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-1 text-muted">Início do período</label>
                      <input type="date" className="form-control form-control-sm" value={form.inicio}
                        onChange={(e) => setForm((f) => ({ ...f, inicio: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-1 text-muted">Fim do período</label>
                      <input type="date" className="form-control form-control-sm" value={form.fim}
                        onChange={(e) => setForm((f) => ({ ...f, fim: e.target.value }))} />
                    </div>
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-1 text-muted">Instituição de ensino</label>
                      <input type="text" className="form-control form-control-sm" value={form.instituicao}
                        onChange={(e) => setForm((f) => ({ ...f, instituicao: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-1 text-muted">Curso</label>
                      <input type="text" className="form-control form-control-sm" value={form.curso}
                        onChange={(e) => setForm((f) => ({ ...f, curso: e.target.value }))} />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label small fw-bold mb-1 text-muted">Supervisor na empresa</label>
                    <input type="text" className="form-control form-control-sm" value={form.supervisor}
                      onChange={(e) => setForm((f) => ({ ...f, supervisor: e.target.value }))} />
                  </div>
                  <div className="mb-1">
                    <label className="form-label small fw-bold mb-1 text-muted">Observação</label>
                    <textarea className="form-control form-control-sm" rows={2} value={form.obs}
                      onChange={(e) => setForm((f) => ({ ...f, obs: e.target.value }))} />
                  </div>
                  {form.avisoInicio && form.confirmarInicio === form.inicio && (
                    <div className="alert small d-flex align-items-start gap-2 mb-2"
                      style={{ backgroundColor: '#fff5da', color: '#8a6d00', border: '1px solid #f0dfa8' }}>
                      <i className="fas fa-triangle-exclamation mt-1"></i>
                      <div>
                        <b>Já existe um período começando em {form.avisoInicio}.</b>{' '}
                        Confira se não é o mesmo termo lançado duas vezes. Para gravar assim mesmo, clique em
                        "Salvar mesmo assim".
                      </div>
                    </div>
                  )}
                  <small className="text-muted">
                    Se o fim informado passar do limite legal de {tetoAnos} anos, o período é gravado mesmo assim
                    e a tela avisa — a exceção da lei (estagiário com deficiência) é decisão do operador.
                  </small>
                </div>
                <div className="modal-footer bg-light">
                  <button type="button" className="btn btn-secondary" onClick={() => setForm(null)} disabled={salvando}>
                    <i className="fas fa-times me-2"></i>Cancelar
                  </button>
                  <button type="button" className="btn btn-success" onClick={salvar} disabled={salvando}>
                    <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>
                    {salvando ? 'Salvando...'
                      : (form.avisoInicio && form.confirmarInicio === form.inicio) ? 'Salvar mesmo assim' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── MODAL: ENCERRAR ─── */}
        {encerrando && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-flag-checkered me-2"></i>Encerrar o estágio
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {encerrando.item.nome} · período {encerrando.item.periodo_atual.inicio} → {encerrando.item.periodo_atual.fim}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setEncerrando(null)} disabled={salvando}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-2">
                    <div className="col-md-8">
                      <label className="form-label small fw-bold mb-1 text-muted">Motivo</label>
                      <input type="text" className="form-control form-control-sm" autoFocus
                        placeholder="efetivação, desistência, desligamento..."
                        value={encerrando.motivo}
                        onChange={(e) => setEncerrando((x) => ({ ...x, motivo: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') encerrar(); }} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold mb-1 text-muted">Encerrado em</label>
                      <input type="date" className="form-control form-control-sm" value={encerrando.quando}
                        onChange={(e) => setEncerrando((x) => ({ ...x, quando: e.target.value }))} />
                    </div>
                  </div>
                  <small className="text-muted">
                    O período sai dos prazos e fica no histórico. O tempo dele continua contando para o limite
                    de {tetoAnos} anos se a pessoa voltar a estagiar na mesma empresa.
                  </small>
                </div>
                <div className="modal-footer bg-light">
                  <button type="button" className="btn btn-secondary" onClick={() => setEncerrando(null)} disabled={salvando}>
                    <i className="fas fa-times me-2"></i>Cancelar
                  </button>
                  <button type="button" className="btn btn-danger" onClick={encerrar} disabled={salvando}>
                    <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-flag-checkered'} me-2`}></i>
                    {salvando ? 'Encerrando...' : 'Confirmar encerramento'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── MODAL: EXCLUIR PERÍODO ─── */}
        {excluindo && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-trash me-2"></i>Excluir o período
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {excluindo.item.nome} · {excluindo.periodo.inicio} → {excluindo.periodo.fim}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setExcluindo(null)} disabled={salvando}></button>
                </div>
                <div className="modal-body small">
                  O período é apagado de vez, junto com o termo em PDF anexado a ele. Use <b>Encerrar</b> quando o
                  estágio terminou de verdade — excluir é só para lançamento errado.
                </div>
                <div className="modal-footer bg-light">
                  <button type="button" className="btn btn-secondary" onClick={() => setExcluindo(null)} disabled={salvando}>
                    <i className="fas fa-times me-2"></i>Cancelar
                  </button>
                  <button type="button" className="btn btn-danger" onClick={excluir} disabled={salvando}>
                    <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-trash'} me-2`}></i>
                    {salvando ? 'Excluindo...' : 'Excluir'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── MODAL: PRÉVIA DO AVISO AO GESTOR (o Enviar mora aqui dentro) ─── */}
        {previa && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-envelope me-2"></i>
                      Aviso ao gestor — {previa.motivo === 'teto' ? `limite legal de ${tetoAnos} anos` : 'fim do termo'}
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {previa.item.nome}{previa.cliente ? ` · ${previa.cliente}` : ''}
                      {previa.marco && ` · ${previa.motivo === 'teto' ? 'limite' : 'vence'} em ${previa.marco}`}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setPrevia(null)} disabled={enviando}></button>
                </div>
                <div className="modal-body">
                  {previa.carregando ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                      <p className="text-muted mt-2 mb-0">Montando a prévia...</p>
                    </div>
                  ) : (
                    <>
                      {previa.motivo === 'teto' ? (
                        <div className="alert small d-flex align-items-start gap-2"
                          style={{ backgroundColor: '#fdeaec', color: '#a11d2b', border: '1px solid #f5c2c7' }}>
                          <i className="fas fa-scale-balanced mt-1"></i>
                          <div>
                            <b>Limite legal de {tetoAnos} anos (Lei 11.788, art. 11) — não cabe renovação.</b>{' '}
                            O e-mail pede ao gestor que decida entre encerrar e efetivar.
                          </div>
                        </div>
                      ) : (
                        <div className="alert alert-light border small">
                          <i className="fas fa-circle-info me-2 text-primary"></i>
                          O termo vigente vence: o e-mail pergunta ao gestor se o estágio continua (aditivo de renovação) ou se encerra.
                        </div>
                      )}
                      <div className="row g-2 mb-3">
                        <div className="col-md-7">
                          <label className="form-label small fw-bold mb-1 text-muted">
                            Destinatários (separe por ; )
                          </label>
                          <div className="input-group input-group-sm">
                            <input type="text" className="form-control form-control-sm" autoFocus
                              value={previa.destinatarios}
                              onChange={(e) => setPrevia((p) => ({ ...p, destinatarios: e.target.value }))}
                              placeholder="gestor@empresa.com.br" />
                            {/* guarda para este estagiário e como padrão da empresa — sem enviar */}
                            <button type="button" className="btn btn-outline-secondary" disabled={previa.salvandoEmail || !previa.destinatarios.trim()}
                              onClick={salvarEmail} title="Guardar este e-mail para os próximos avisos (não envia nada)">
                              <i className={`fas ${previa.salvandoEmail ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-1`}></i>Salvar
                            </button>
                          </div>
                          <small className="text-muted">
                            {previa.destinatarios_origem === 'pessoa' ? 'E-mail salvo para este estagiário. '
                              : previa.destinatarios_origem === 'empresa' ? 'E-mail padrão salvo para a empresa. ' : ''}
                            O grupo de e-mail da empresa entra em cópia automaticamente no envio.
                          </small>
                        </div>
                        <div className="col-md-5">
                          <label className="form-label small fw-bold mb-1 text-muted">Enviar com a conta de</label>
                          <select className="form-select form-select-sm" value={previa.operador_id}
                            onChange={(e) => setPrevia((p) => ({ ...p, operador_id: e.target.value }))}>
                            <option value="">Selecione a operadora...</option>
                            {operadores.map((o) => (
                              <option key={o.id} value={o.id} disabled={!o.tem_senha}>
                                {o.nome}{!o.tem_senha ? ' (sem senha de app)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="mb-2">
                        <label className="form-label small fw-bold mb-1 text-muted">Assunto</label>
                        <input type="text" className="form-control form-control-sm" value={previa.assunto || ''}
                          onChange={(e) => setPrevia((p) => ({ ...p, assunto: e.target.value }))} />
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="small fw-bold text-muted">Corpo do e-mail</span>
                        <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: '.78rem', textDecoration: 'none' }}
                          onClick={() => setEditandoCorpo((v) => !v)}>
                          <i className={`fas ${editandoCorpo ? 'fa-eye' : 'fa-pen'} me-1`}></i>
                          {editandoCorpo ? 'Concluir edição' : 'Editar o texto'}
                        </button>
                      </div>
                      {editandoCorpo ? (
                        /* edição no texto formatado (01/10/2026): o operador digita como num
                           documento; o HTML é lido do próprio bloco ao sair dele */
                        <div className="border rounded p-2" style={{ height: '340px', overflow: 'auto', outline: '2px solid #00b5cc33', background: '#fff' }}
                          contentEditable suppressContentEditableWarning
                          ref={(el) => { if (el && !el.dataset.pronto) { el.innerHTML = previa.corpo_html || ''; el.dataset.pronto = '1'; } }}
                          onBlur={(e) => { const html = e.currentTarget.innerHTML; setPrevia((p) => ({ ...p, corpo_html: html })); }}
                          onInput={(e) => { const html = e.currentTarget.innerHTML; setPrevia((p) => ({ ...p, corpo_html: html })); }} />
                      ) : (
                        <div className="border rounded" style={{ height: '340px', overflow: 'hidden' }}>
                          <iframe title="Prévia do aviso de estágio" srcDoc={previa.corpo_html}
                            style={{ width: '100%', height: '100%', border: 0 }} />
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-footer bg-light">
                  <button type="button" className="btn btn-secondary" onClick={() => setPrevia(null)} disabled={enviando}>
                    <i className="fas fa-times me-2"></i>Cancelar
                  </button>
                  {!previa.carregando && (
                    <button type="button" className="btn btn-success" onClick={enviar}
                      disabled={enviando || !previa.operador_id || !previa.destinatarios.trim()}
                      title={!previa.operador_id ? 'Escolha a operadora que assina o envio' : !previa.destinatarios.trim() ? 'Informe o destinatário' : 'Enviar o aviso'}>
                      <i className={`fas ${enviando ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                      {enviando ? 'Enviando...' : 'Confirmar envio'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      {rel && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-file-lines me-2"></i>Relatório geral de estagiários
                  </h5>
                  <small style={{ color: '#6c757d' }}>{dados?.cliente || ''} · todos os estagiários numa planilha, com o resumo no e-mail</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setRel(null)} disabled={relOcupado === 'enviar'}></button>
              </div>
              <div className="modal-body">
                {!relPrevia ? (<>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Para</label>
                      <input type="text" className="form-control form-control-sm" placeholder="rh@cliente.com.br"
                        value={rel.para} onChange={(e) => mudarRel('para', e.target.value)} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Cópia (opcional)</label>
                      <input type="text" className="form-control form-control-sm" value={rel.copia}
                        onChange={(e) => mudarRel('copia', e.target.value)} />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label small fw-semibold">Enviar como</label>
                      <select className="form-select form-select-sm" value={rel.operador_id}
                        onChange={(e) => mudarRel('operador_id', e.target.value)}>
                        <option value="">— escolha —</option>
                        {rel.operadores.map((o) => (
                          <option key={o.id} value={o.id} disabled={!o.pronto}>{o.nome}{o.pronto ? '' : ' (sem senha de app)'}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-7 d-flex align-items-end gap-2">
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={salvarEmailsRel}
                        disabled={!!relOcupado || !rel.para.trim()} title="Guardar Para e Cópia para os próximos envios desta empresa">
                        <i className={`fas ${relOcupado === 'salvar' ? 'fa-spinner fa-spin' : 'fa-save'} me-1`}></i>Salvar e-mails
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-success" onClick={baixarRelatorio} disabled={!!relOcupado}
                        title="Baixar a planilha para conferir">
                        <i className={`fas ${relOcupado === 'baixar' ? 'fa-spinner fa-spin' : 'fa-file-excel'} me-1`}></i>Baixar planilha
                      </button>
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Assunto</label>
                      <input type="text" className="form-control form-control-sm" value={rel.assunto}
                        onChange={(e) => mudarRel('assunto', e.target.value)} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Mensagem (editável)</label>
                      <textarea className="form-control form-control-sm" rows={8} value={rel.mensagem}
                        onChange={(e) => mudarRel('mensagem', e.target.value)} />
                      <div className="form-text">
                        Abaixo da mensagem o e-mail traz os números do mês e a lista de quem precisa de atenção (termo vencendo,
                        limite de 2 anos, sem termo). A planilha completa vai anexada.
                      </div>
                    </div>
                  </div>
                  {rel.historico.length > 0 && (
                    <div className="small text-muted mt-2">
                      <i className="fas fa-envelope-circle-check me-1 text-success"></i>
                      Enviado antes: {rel.historico.map((h) => `${h.em} para ${h.para}${h.por ? ` (${h.por})` : ''}`).join(' · ')}
                    </div>
                  )}
                </>) : (<>
                  <div className="small mb-2">
                    <div><b>Assunto:</b> {relPrevia.assunto}</div>
                    <div><b>De:</b> {relPrevia.de}</div>
                    <div><b>Para:</b> {relPrevia.para || <span className="text-danger">informe o destinatário</span>}</div>
                    {relPrevia.copia && <div><b>Cópia:</b> {relPrevia.copia}</div>}
                    <div><b>Anexo:</b> <i className="fas fa-file-excel text-success me-1"></i>{relPrevia.anexo}</div>
                  </div>
                  <div className="border rounded" style={{ maxHeight: 380, overflow: 'auto' }}>
                    <iframe title="Prévia do relatório de estagiários" srcDoc={relPrevia.corpo_previa}
                      sandbox="" style={{ width: '100%', height: 380, border: 0 }} />
                  </div>
                </>)}
              </div>
              <div className="modal-footer bg-light">
                {!relPrevia ? (<>
                  <button className="btn btn-secondary" onClick={() => setRel(null)}><i className="fas fa-times me-2"></i>Fechar</button>
                  <button className="btn btn-primary" onClick={verPreviaRel} disabled={!!relOcupado || !rel.para.trim() || !rel.operador_id}>
                    <i className={`fas ${relOcupado === 'previa' ? 'fa-spinner fa-spin' : 'fa-eye'} me-2`}></i>Visualizar e-mail
                  </button>
                </>) : (<>
                  <span className="me-auto small text-muted">A planilha vai anexada.</span>
                  <button className="btn btn-secondary" onClick={() => setRelPrevia(null)} disabled={relOcupado === 'enviar'}>
                    <i className="fas fa-pen me-2"></i>Voltar e editar
                  </button>
                  <button className="btn btn-success" onClick={enviarRel} disabled={relOcupado === 'enviar'}>
                    <i className={`fas ${relOcupado === 'enviar' ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                    {relOcupado === 'enviar' ? 'Enviando...' : 'Enviar'}
                  </button>
                </>)}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default BpoRhEstagio;
